import type { EngineLicense, LicenseModel, SystemLicensing } from "../domain/licensing";
import { countsFue } from "../domain/licensing";
import { landscapeById, landscapes } from "./landscapes";

// ============================================================
//  Einstufung der Systeme Arvatos (Bauauftrag AP1).
//  Belegt = aus unseren Terminen bzw. eindeutig aus der Produktnatur.
//  Nicht belegt = Annahme bis zum Gegenbeweis, in der App als solche gekennzeichnet.
// ============================================================

const GTS_ENGINE: EngineLicense = {
  name: "SAP Global Trade Services",
  einheit: "Zolldokumente / Screenings pro Jahr",
  wert: null,
  quelle: "offen",
  herkunft:
    "Die GTS-Metrik ist öffentlich nicht belastbar dokumentiert — der vereinbarte Wert muss aus Arvatos Vertrag kommen.",
};

const HCM_METRIK: EngineLicense = {
  name: "S/4HANA Private Cloud Edition für HCM",
  einheit: "Employee (User-Metrik)",
  wert: null,
  quelle: "offen",
  herkunft:
    "HCM wird unter Private Cloud über eine User-Metrik lizenziert, die faktisch einem Employee entspricht — über eigene Vertragspositionen, nicht über FUE.",
};

const EWM_ENGINE: EngineLicense = {
  name: "SAP EWM (Advanced)",
  einheit: "je 5.000 Lieferpositionen",
  wert: null,
  quelle: "offen",
  herkunft:
    "EWM Basic ist im S/4HANA-Kern enthalten; nur die Advanced-Ausprägung ist zusätzlich lizenzpflichtig und wird je 5.000 Lieferpositionen bemessen. Welche Ausprägung Arvato nutzt, ist offen.",
};

const TM_ENGINE: EngineLicense = {
  name: "SAP TM (Advanced)",
  einheit: "Freight Spend oder Revenue",
  wert: null,
  quelle: "offen",
  herkunft:
    "Der Standard-Versand ist im S/4HANA-Kern enthalten; TM Advanced wird über Freight Spend oder Revenue bemessen. Welche Ausprägung Arvato nutzt, ist offen.",
};

const FUE_ANNAHME =
  "Als S/4HANA-System eingestuft — Annahme bis zum Gegenbeweis. Im Termin zu bestätigen.";

export const systemLicensing: SystemLicensing[] = [
  {
    sid: "GSP",
    model: "eigene-metrik",
    engine: GTS_ENGINE,
    belegt: true,
    note: 'GTS-Produktivsystem — belegt aus dem Termin am 04.09.: „GSP ist für uns die interne Bezeichnung vom GTS-Produktivsystem."',
  },
  {
    sid: "HCM",
    model: "eigene-metrik",
    engine: HCM_METRIK,
    belegt: true,
    note: "SAP HCM — läuft unter Private Cloud auf einer eigenen Employee-Metrik, nicht auf FUE.",
  },
  {
    sid: "FIORI",
    model: "keine",
    belegt: true,
    note: "Fiori-Frontend-Server — Bestandteil der Plattform, trägt keine eigene Lizenz.",
  },
  {
    sid: "HANA Cockpit",
    model: "keine",
    belegt: true,
    note: "Administrationswerkzeug — trägt keine eigene Lizenz.",
  },
  { sid: "CS7", model: "fue", belegt: false, note: FUE_ANNAHME },
  { sid: "CSA", model: "fue", belegt: false, note: FUE_ANNAHME },
  { sid: "JP5", model: "fue", belegt: false, note: FUE_ANNAHME },
  { sid: "CS2", model: "fue", belegt: false, note: FUE_ANNAHME },
  { sid: "FP6", model: "fue", belegt: false, note: FUE_ANNAHME },
  {
    sid: "GP7",
    model: "fue+engine",
    engine: EWM_ENGINE,
    belegt: false,
    note: "Größte Landschaft (110 Mandanten). Annahme: hier läuft EWM Advanced. Der Kunde nennt ein EWM-System, hat es aber keinem Kürzel zugeordnet — die Zuordnung ist im Termin zu bestätigen. Die FUE-Zählung der Nutzer bleibt davon unberührt.",
  },
  {
    sid: "TPM",
    model: "fue+engine",
    engine: TM_ENGINE,
    belegt: false,
    note: "Annahme: hier läuft TM Advanced. Der Kunde nennt Transportmanagement als Engine-System, hat es aber keinem Kürzel zugeordnet — die Zuordnung ist im Termin zu bestätigen. Die FUE-Zählung der Nutzer bleibt davon unberührt.",
  },
  { sid: "ASP", model: "fue", belegt: false, note: FUE_ANNAHME },
  { sid: "AWP", model: "fue", belegt: false, note: FUE_ANNAHME },
  { sid: "EP6", model: "fue", belegt: false, note: FUE_ANNAHME },
  { sid: "GPY", model: "fue", belegt: false, note: FUE_ANNAHME },
  { sid: "JP6", model: "fue", belegt: false, note: FUE_ANNAHME },
];

const bySid = new Map(systemLicensing.map((s) => [s.sid, s]));

/** Einstufung eines Systems. Unbekannte SIDs gelten als FUE-System (Annahme). */
export function licensingBySid(sid: string): SystemLicensing {
  return bySid.get(sid) ?? { sid, model: "fue", belegt: false, note: FUE_ANNAHME };
}

/** Trägt dieses System FUE bei? (Kategorie A oder B) */
export function isFueRelevantSid(sid: string): boolean {
  return countsFue(licensingBySid(sid).model);
}

/** Alle Systeme einer Landschaft mit ihrer Einstufung. */
export function landscapeLicensing(landscapeId: string): SystemLicensing[] {
  return (landscapeById(landscapeId)?.systems ?? []).map(licensingBySid);
}

/**
 * Das Lizenzmodell einer Landschaft = das ihres führenden Systems.
 * Die Konten liegen am führenden System, deshalb entscheidet dessen Modell
 * darüber, ob die Landschaft FUE beiträgt.
 */
export function landscapeModel(landscapeId: string): LicenseModel {
  const l = landscapeById(landscapeId);
  const lead = l?.systems[0];
  return lead ? licensingBySid(lead).model : "fue";
}

export function isFueRelevantLandscape(landscapeId: string): boolean {
  return countsFue(landscapeModel(landscapeId));
}

/** Landschaften, die FUE beitragen — Basis aller FUE-Summen und -Kurven. */
export const fueLandscapes = landscapes.filter((l) => !l.open && isFueRelevantLandscape(l.id));

/** Landschaften mit eigener bzw. ohne Metrik — sichtbar, aber ohne FUE-Beitrag. */
export const nonFueLandscapes = landscapes.filter((l) => !l.open && !isFueRelevantLandscape(l.id));

/**
 * Offene Zuordnung: Der Kunde nennt DREI Systeme auf Engine-Lizenz-Basis
 * (GTS, EWM, Transportmanagement). Zugeordnet ist bisher nur GTS = GSP.
 * Die App rät die beiden anderen nicht — sie weist die Lücke aus.
 */
export interface OpenEngineAssignment {
  engine: string;
  einheit: string;
  quelle: string;
  status: string;
}

export const openEngineAssignments: OpenEngineAssignment[] = [
  {
    engine: "SAP EWM (Advanced)",
    einheit: "je 5.000 Lieferpositionen",
    quelle:
      "Im Termin genannt: „Wir haben drei Systeme, die auf Engine-Lizenz-Basis laufen … dann das EWM-System.\"",
    status: "Als Annahme auf GP7 gelegt — Kürzel und Ausprägung (Basic/Advanced) im Termin zu bestätigen.",
  },
  {
    engine: "SAP TM (Advanced)",
    einheit: "Freight Spend oder Revenue",
    quelle: "Im Termin ergänzt: Transportmanagement.",
    status: "Als Annahme auf TPM gelegt — Kürzel und Ausprägung (Basic/Advanced) im Termin zu bestätigen.",
  },
];
